import { Injectable } from '@angular/core';
import * as angular from 'angular';

export interface ICouple {
  'angularjs-component'?: boolean | undefined;
  'angular-component'?: boolean | undefined;
  data?: any;
}
@Injectable({
  providedIn: 'root',
})
export class UiGridCommonService {
  static _instance: UiGridCommonService | null = null; // Singleton instance
  private _couples: { [id: string]: ICouple } = {}; // List of all couples
  private _uniqueIds: string[] = [];

  constructor() {
    const win = window as any;

    if (UiGridCommonService._instance) {
      return UiGridCommonService._instance;
    }

    // If global instance exists, use that instead
    if (win.__uiGridCommonService) {
      return (UiGridCommonService._instance = win.__uiGridCommonService);
    }
    // Otherwise, set this instance as the singleton
    UiGridCommonService._instance = this;
  }

  static isAngularjsModuleDefined(module: string): boolean {
    let isDefined = false;
    try {
      angular.module(module);
      isDefined = true;
    } catch (e) {
      //ignore
    }
    return isDefined;
  }

  static initAngularjsApp(module: string) {
    const upgrade = angular.module(module, [
      'ui.grid',
      'ui.grid.grouping',
      'ui.grid.pagination',
      'ui.grid.autoResize',
      'ui.grid.resizeColumns',
      'ui.grid.infiniteScroll',
      'ui.grid.pinning',
      'ui.grid.selection',
      'ui.grid.expandable',
      'ui.grid.moveColumns',
    ]);
    upgrade.service('uiGridCommonService', UiGridCommonService);
    upgrade.directive('dmUiGridLib', [
      'uiGridCommonService',
      (uiGridCommonService) => {
        return {
          restrict: 'E',
          scope: {},
          template: `<div ui-grid="options" class="grid ui-grid-selectable" ng-show="options.isVisible"
                 ui-grid-grouping
                 ui-grid-auto-resize
                 ui-grid-infinite-scroll
                 ui-grid-resize-columns
                 ui-grid-pinning
                 ui-grid-selection
                 ui-grid-pagination
                 ui-grid-move-columns>
                          <div class="data-loading-board" ng-if="options.dataLoading">Loading data ...</div>
                          <div class="watermark-board" ng-if="!options.dataLoading && !options.data.length">No data available</div>
                        </div>`,
          link: (scope: any) => {
            scope.gridId = uiGridCommonService.getUniqueIdForCouple(
              'angularjs-component'
            );
            scope.options = uiGridCommonService.getOptionsById(scope.gridId);
            scope.digestInterval = setInterval(() => {
              scope.$digest();
            }, 500);

            scope.$on('$destroy', () => {
              clearInterval(scope.digestInterval);
              uiGridCommonService.removeCoupleById(scope.gridId);
            });
          },
        };
      },
    ]);
  }

  static bootstrapAngularjsApp(element: any, module: string) {
    try {
      angular.bootstrap(element, [module], {
        strictDi: true,
      });
    } catch (error) {
      console.error(`Failed to bootstrap ${module}: `, error);
    }
  }

  static compileAngularjsApp(target: any) {
    try {
      const source = document.querySelector('[ng-app]')!;
      const $injector = window.angular.element(source).injector();
      const $compile = $injector.get('$compile');
      const $rootScope = $injector.get('$rootScope');

      const scope = $rootScope.$new(true);
      $compile(target)(scope);
      scope.$apply();
    } catch (err) {
      console.error(
        'Failed to compile AngularJS directive in Shadow DOM:',
        err
      );
    }
  }

  static isElementBootstrapped(element: any) {
    const angularElem = angular.element(element);
    return angularElem && !!angularElem.injector();
  }

  static getAngularJsMainModuleName() {
    const ngAppEl = document.querySelector('[ng-app]');
    if (ngAppEl) return ngAppEl.getAttribute('ng-app');
    return null;
  }

  public getUniqueId(): string {
    const id =
      Date.now().toString(36) + Math.random().toString(36).substring(2);
    if (this._uniqueIds.includes(id)) return this.getUniqueId();
    else this._uniqueIds.push(id);
    return id;
  }

  public getUniqueIdForCouple(
    type: 'angularjs-component' | 'angular-component'
  ): string {
    let id = '';
    let freeCouple;
    for (const [key, value] of Object.entries(this._couples)) {
      if (!value[type]) {
        freeCouple = value;
        id = key;
        break;
      }
    }

    if (freeCouple) {
      freeCouple[type] = true;
      return id;
    } else {
      id = this.getUniqueId();
      this._couples[id] = { [type]: true };
      return id;
    }
  }

  public getCoupleById(id: string): ICouple {
    return this._couples[id];
  }

  public getOptionsById(id: string): any {
    return this.getCoupleById(id).data;
  }

  public removeCoupleById(id: string): any {
    if (this._couples[id]) {
      delete this._couples[id];
    }
  }

  public setOptionsForId(id: string, options: any): void {
    this.getCoupleById(id).data = options;
  }
}
